import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

interface HeaderProps {
  onMenuToggle: () => void;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アプリヘッダー。モバイルメニュートグル・ダークモード切替・ユーザーアバター・ログアウトメニューを配置
 * @graph-connects useAuth [calls] UseAuthを呼び出す
 * @graph-connects useTheme [calls] UseThemeを呼び出す
 */
export function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout, isLoggingOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /** @graph-connects none */
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header
      className="sticky top-0 z-[var(--z-sticky)] flex h-14 items-center justify-between border-b px-4"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(var(--glass-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-blur))',
        borderColor: 'var(--color-border)',
      }}
    >
      {/* 左: モバイルメニュー + ロゴ */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="btn-ghost rounded-lg p-1.5 md:hidden"
          aria-label="メニュー"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Quality Atelier
        </span>
      </div>

      {/* 右: テーマトグル + ユーザー */}
      <div className="flex items-center gap-2">
        <button
          onClick={toggleTheme}
          className="btn-ghost rounded-lg p-2"
          aria-label={isDark ? 'ライトモードに切替' : 'ダークモードに切替'}
        >
          {isDark ? (
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          )}
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => {
              setIsUserMenuOpen(!isUserMenuOpen);
            }}
            className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-[var(--color-bg-hover)]"
          >
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full" />
            ) : (
              <div
                className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium"
                style={{ background: 'var(--color-primary-muted)', color: 'var(--color-primary)' }}
              >
                {user?.name.charAt(0) ?? '?'}
              </div>
            )}
            <span
              className="hidden text-sm md:block"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {user?.name}
            </span>
          </button>

          {isUserMenuOpen && (
            <div
              className="glass-card absolute right-0 top-full mt-2 w-48 p-1"
              style={{ animation: 'fadeIn 0.15s ease', zIndex: 'var(--z-dropdown)' }}
            >
              <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {user?.name}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {user?.email}
                </p>
              </div>
              <button
                onClick={() => {
                  logout();
                }}
                disabled={isLoggingOut}
                className="btn-ghost mt-1 w-full justify-start rounded-md px-3 py-1.5 text-sm"
                style={{ color: 'var(--color-error)' }}
              >
                {isLoggingOut ? 'ログアウト中...' : 'ログアウト'}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
